import {
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Row,
  Select,
} from 'antd';
import {
  DataFeedFirmographicsFilters,
  DataFeedForm,
  DataFeedFormField,
} from './DataFeedForm';
import { EntitySearchSelectFormItem } from './EntitySearchSelectFormItem';
import { URLSelectorFormItem } from './URLSelectorFormItem';
import { CompanyStatusSelect } from './CompanyStatusSelect';
import { SectorIndustrySelector } from './SectorIndustrySelector';
import { ListExpertCollectionSelect } from './ListExpertCollectionSelect';
import { CategoryFundingRoundsSelect } from './CategoryFundingRoundsSelect';
import {
  ContinentSelect,
  CountryStateCitySelector,
  RegionSelect,
} from './CountryStateCitySelector';
import { InvestorTypeSelect } from './InvestorTypeSelect';
import { EntityTypeStatus } from './FiltersConfirmation';
import { TagGroupSelect } from './TagGroupSelect';

export const APIFilters = () => {
  const { getFieldValue } = Form.useFormInstance<DataFeedForm>();
  const selectedDataset: string = getFieldValue(DataFeedFormField.dataset);
  const basePathName = [DataFeedFormField.filters, selectedDataset];
  return (
    <Row gutter={[8, 8]}>
      <Col span={12}>
        <Card bordered={false} title="Entity Filters">
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <EntitySearchSelectFormItem
                label="Organization IDs"
                name={[...basePathName, DataFeedFirmographicsFilters.orgIds]}
              />

              <EntitySearchSelectFormItem
                label="Organization Names"
                name={[...basePathName, DataFeedFirmographicsFilters.orgNames]}
              />

              <URLSelectorFormItem
                name={[...basePathName, DataFeedFirmographicsFilters.urls]}
                label="URLs"
                placeholder="Add a valid URL"
              />

              <CompanyStatusSelect
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.companyStatus,
                ]}
                label="Company Status"
                placeholder="Select a status"
              />

              <Form.Item
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.descriptions,
                ]}
                label="Descriptions"
              >
                <Select
                  mode="tags"
                  placeholder="Search organization descriptions"
                />
              </Form.Item>

              <Form.Item
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.entityType,
                ]}
                label="Entity Type"
                initialValue={{
                  value: EntityTypeStatus.COMPANIES_AND_INVESTORS,
                  label: EntityTypeStatus.COMPANIES_AND_INVESTORS,
                }}
              >
                <Select
                  options={[
                    {
                      value: EntityTypeStatus.COMPANIES_AND_INVESTORS,
                      label: EntityTypeStatus.COMPANIES_AND_INVESTORS,
                    },
                    {
                      value: EntityTypeStatus.COMPANIES,
                      label: EntityTypeStatus.COMPANIES,
                    },
                    {
                      value: EntityTypeStatus.INVESTORS,
                      label: EntityTypeStatus.INVESTORS,
                    },
                  ]}
                  labelInValue
                />
              </Form.Item>

              <Form.Item
                name={[...basePathName, DataFeedFirmographicsFilters.tier1Only]}
                valuePropName="checked"
              >
                <Checkbox>Include only tier 1 organizations</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Col>
      <Col span={12}>
        <Card bordered={false} title="Sector Filters">
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <SectorIndustrySelector filterName={basePathName} />
            </Col>
          </Row>
        </Card>
        <Card bordered={false} title="Landscape Filters" className="mt-2">
          <TagGroupSelect
            idTagGroup={2}
            name={[...basePathName, DataFeedFirmographicsFilters.technologyIds]}
            label="Technologies"
            placeholder="Select technologies"
          />
          <TagGroupSelect
            idTagGroup={1}
            name={[
              ...basePathName,
              DataFeedFirmographicsFilters.businessModelIds,
            ]}
            label="Business Models"
            placeholder="Select business models"
          />
          <TagGroupSelect
            idTagGroup={4}
            name={[
              ...basePathName,
              DataFeedFirmographicsFilters.awardsAndSpotlightsIds,
            ]}
            label="Awards & Spotlights"
            placeholder="Select awards & spotlights"
          />
        </Card>
      </Col>
      <Col span={12}>
        <Card bordered={false} title="Collection Filters">
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <ListExpertCollectionSelect
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.collectionIds,
                ]}
                label="Expert Collections"
                placeholder="Select an expert collection"
              />
            </Col>
          </Row>
        </Card>
      </Col>
      <Col span={12}>
        <Card bordered={false} title="Financial Filters">
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <CategoryFundingRoundsSelect
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.lastFundingRoundCategoryIds,
                ]}
                label="Last Funding Round Categories"
                placeholder="Select a category"
              />
            </Col>
            <Col span={12}>
              <Form.Item
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.minTotalFundingInMillions,
                ]}
                label="Min Total Funding (in millions USD)"
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    message: 'Value must be 0 or greater',
                  },
                ]}
              >
                <InputNumber
                  className="min-w-[200px]"
                  placeholder="Enter amount"
                  step={0.1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.maxTotalFundingInMillions,
                ]}
                label="Max Total Funding (in millions USD)"
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    message: 'Value must be 0 or greater',
                  },
                ]}
              >
                <InputNumber
                  className="min-w-[200px]"
                  placeholder="Enter amount"
                  step={0.1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.minDealDate,
                ]}
                label="Min Deal Date"
              >
                <DatePicker />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.maxDealDate,
                ]}
                label="Max Deal Date"
              >
                <DatePicker />
              </Form.Item>
            </Col>
            <Col span={24}>
              <InvestorTypeSelect
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.investorTypeIds,
                ]}
                label="Investor types"
                placeholder="Select types of investors that have invested in these organizations"
              />
            </Col>
            <Col span={12}>
              <Form.Item
                name={[...basePathName, DataFeedFirmographicsFilters.vcBacked]}
                label="VC Backed"
              >
                <Select
                  className="max-w-[90px]"
                  placeholder="Yes/No"
                  allowClear
                  options={[
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' },
                  ]}
                  labelInValue
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Col>
      <Col span={24}>
        <Card bordered={false} title="Location Filters">
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <ContinentSelect
                name={[
                  ...basePathName,
                  DataFeedFirmographicsFilters.continentIds,
                ]}
                label="Continents"
                placeholder="Select a continent"
              />
            </Col>
            <Col span={12}>
              <RegionSelect
                name={[...basePathName, DataFeedFirmographicsFilters.regionIds]}
                label="Regions"
                placeholder="Select a region"
              />
            </Col>
            <Col span={24}>
              <CountryStateCitySelector names={basePathName} />
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );
};
